import { Directive, HostListener, inject, Input, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AsyncOutputJob, OutputJobCtx } from '@elderbyte/ngx-imprimo'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { filter, map, startWith, switchMap } from 'rxjs/operators'
import { NewReportJob, PrintOptions, ReportSpec } from '../report/report-job'
import { ReportJobService } from '../report/report-job.service'

export class PrintOptionsContext {
  public static empty(): PrintOptionsContext {
    return new PrintOptionsContext(null)
  }

  public static from(options: PrintOptions): PrintOptionsContext {
    return new PrintOptionsContext(options)
  }

  constructor(public readonly options: PrintOptions) {}

  public get isPrinterConfigured(): boolean {
    return !!this.options && !!this.options.printerId
  }
}

@Directive({
  selector: '[appPrintCommand]',
})
export class PrintCommandDirective {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly reportSpec$ = new BehaviorSubject<ReportSpec>(null)
  public readonly printOptions$ = new BehaviorSubject<PrintOptionsContext>(
    PrintOptionsContext.empty()
  )

  public readonly newOutputTask$ = new BehaviorSubject<NewReportJob>(null)

  public readonly asyncJob$ = new BehaviorSubject<AsyncOutputJob>(null)

  private readonly reportJobService = inject(ReportJobService)

  /***************************************************************************
   *                                                                         *
   * Host Bindings                                                           *
   *                                                                         *
   **************************************************************************/

  @Input()
  public disabled = false

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    combineLatest([
      this.reportSpec$.pipe(filter((spec) => !!spec)),
      this.printOptions$.pipe(filter((options) => !!options.options)),
    ])
      .pipe(
        takeUntilDestroyed(),
        map(([reportSpec, printOptions]) => NewReportJob.print(reportSpec, printOptions.options))
      )
      .subscribe((newTask) => {
        this.newOutputTask$.next(newTask)
        // Reset current async job if settings or input changes!
        this.asyncJob$.next(this.buildEmptyJob())
      })
  }

  /***************************************************************************
   *                                                                         *
   * Host Listeners                                                          *
   *                                                                         *
   **************************************************************************/

  @HostListener('click') click() {
    if (!this.disabled) {
      if (this.canStartNewPrint(this.printOptions$.getValue())) {
        this.startNewPrintTask()
      }
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set reportSpec(reportSpec: ReportSpec) {
    this.reportSpec$.next(reportSpec)
  }

  @Input()
  public set printOptions(printOptions: PrintOptions) {
    this.printOptions$.next(PrintOptionsContext.from(printOptions))
  }

  @Input()
  public set printerId(printerId: string) {
    this.printOptions = PrintOptions.printer(printerId)
  }

  @Output()
  public get canPrint$(): Observable<boolean> {
    return this.printOptions$.pipe(map((options) => this.canStartNewPrint(options)))
  }

  @Output()
  public get taskStatus$(): Observable<OutputJobCtx> {
    return this.asyncJob$.pipe(
      filter((asyncJob) => !!asyncJob),
      switchMap((asyncJob) => asyncJob.context$),
      startWith(OutputJobCtx.empty())
    )
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private canStartNewPrint(printOptions: PrintOptionsContext): boolean {
    return printOptions.isPrinterConfigured
  }

  private startNewPrintTask(): void {
    const task = this.newOutputTask$.getValue()
    if (task) {
      this.reportJobService
        .createAsync(task)
        .subscribe((asyncTask) => this.asyncJob$.next(asyncTask))
    } else {
      this.log.warn('NewOutputTask not ready!')
    }
  }

  private buildEmptyJob(): AsyncOutputJob {
    return AsyncOutputJob.of(null, null)
  }
}
